import { CheckedAddress, NormalizedNumber } from '@sparkdotfi/common-universal'
import { gnosis } from 'viem/chains'

import { ChainConfigEntry, SavingsStatsItem } from '../../types'
import { sortSavingsAccounts } from '../../utils'
import { chainLogo, tokenLogos } from './assets'
import { createTokenDefinitionRepository } from '@/domain/token-repository/TokenRepository'
import { TokenDefinition } from '@/domain/types/TokenDefinition'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { skySavingsGeneralStatsQueryOptionsFactory } from '@/features/savings/data/general-stats-query/skySavings'
import { skySavingsBackedByOptions } from '@/features/savings/data/savings-charts/backed-by-query/skySavingsBackedByQuery'
import { gnosisSavingsDaiConverterQuery } from '@/features/savings/logic/savings-converters/gnosisSavingsConverter'
import { fetchSdaiOracleInfoGnosis } from '@/features/sparklend/common/data/oracles/oracleInfoFetchers'
import {
  historicBorrowRateGnosisQueryOptions,
  historicSupplyRateGnosisQueryOptions,
} from '@/features/sparklend/market-details/data/market-charts/gnosis'

const xdai = new TokenDefinition({
  symbol: TokenSymbol('XDAI'),
  name: 'xDAI',
  decimals: 18,
  address: CheckedAddress.EEEE(),
  logo: tokenLogos.xdai,
})

const wxdai = new TokenDefinition({
  symbol: TokenSymbol('WXDAI'),
  name: 'Wrapped xDAI',
  decimals: 18,
  address: CheckedAddress('0xe91D153E0b41518A2Ce8Dd3D7944Fa863463a97d'),
  logo: tokenLogos.wxdai,
})

const sdai = new TokenDefinition({
  symbol: TokenSymbol('sDAI'),
  name: 'Savings Dai',
  decimals: 18,
  address: CheckedAddress('0xaf204776c7245bF4147c2612BF6e5972Ee483701'),
  logo: tokenLogos.sdai,
})

const weth = new TokenDefinition({
  symbol: TokenSymbol('WETH'),
  name: 'Wrapped Ether',
  decimals: 18,
  address: CheckedAddress('0x6A023CCd1ff6F2045C3309768eAd9E68F978f6e1'),
  logo: tokenLogos.weth,
})

const wsteth = new TokenDefinition({
  symbol: TokenSymbol('wstETH'),
  name: 'Lido Staked ETH',
  decimals: 18,
  address: CheckedAddress('0x6C76971f98945AE98dD7d4DFcA8711ebea946eA6'),
  logo: tokenLogos.wsteth,
})

const gno = new TokenDefinition({
  symbol: TokenSymbol('GNO'),
  name: 'Gnosis Token',
  decimals: 18,
  address: CheckedAddress('0x9C58BAcC331c9aa871AFD802DB6379a98e80CEdb'),
  logo: tokenLogos.gno,
})

const usdc = new TokenDefinition({
  symbol: TokenSymbol('USDC'),
  name: 'Circle USD (Legacy)',
  decimals: 6,
  address: CheckedAddress('0xDDAfbb505ad214D7b80b1f830fcCc89B60fb7A83'),
  logo: tokenLogos.usdc,
})

const usdce = new TokenDefinition({
  symbol: TokenSymbol('USDC.e'),
  name: 'Circle USD (Bridged)',
  decimals: 6,
  address: CheckedAddress('0x2a22f9c3b484c3629090FeED35F17Ff8F88f76F0'),
  logo: tokenLogos['usdc.e'],
})

const usdt = new TokenDefinition({
  symbol: TokenSymbol('USDT'),
  name: 'Tether USD',
  decimals: 6,
  address: CheckedAddress('0x4ECaBa5870353805a9F068101A40E0f32ed605C6'),
  logo: tokenLogos.usdt,
})

const eure = new TokenDefinition({
  symbol: TokenSymbol('EURe'),
  name: 'Monerium EURO',
  decimals: 18,
  address: CheckedAddress('0xcB444e90D8198415266c6a2724b7900fb12FC56E'),
  logo: tokenLogos.eure,
})

export const gnosisConfig: ChainConfigEntry = {
  originChainId: gnosis.id,
  meta: {
    name: 'Gnosis',
    logo: chainLogo,
  },
  tokenDefinitionRepository: createTokenDefinitionRepository([
    xdai,
    wxdai,
    sdai,
    weth,
    wsteth,
    gno,
    usdc,
    usdce,
    usdt,
    eure,
  ]),
  nativeAssetInfo: {
    nativeAssetName: 'XDAI',
    wrappedNativeAssetSymbol: TokenSymbol('WXDAI'),
    wrappedNativeAssetAddress: CheckedAddress('0xe91D153E0b41518A2Ce8Dd3D7944Fa863463a97d'),
    nativeAssetSymbol: TokenSymbol('XDAI'),
    minRemainingNativeAssetBalance: NormalizedNumber(0.1),
    nativeAssetLogo: tokenLogos.xdai,
  },
  tokensWithMalformedApprove: [],
  tokensRequiringResetApproval: [],
  contractsRequiringUsdtResetApproval: [],
  permitSupport: {
    [gno.address]: false,
    [wxdai.address]: false,
    [sdai.address]: false,
    [weth.address]: false,
    [wsteth.address]: false,
    [usdc.address]: false,
    [usdt.address]: false,
    [eure.address]: false,
  },
  sparkLend: {
    defaultAssetToBorrow: wxdai.symbol,
    easyBorrow: false,
    easyBorrowTokensToBorrow: [wxdai.symbol],
    sllManagedMarkets: [],
    supplyRateQueryOptions: historicSupplyRateGnosisQueryOptions,
    borrowRateQueryOptions: historicBorrowRateGnosisQueryOptions,
    oracles: {
      [eure.symbol]: {
        type: 'underlying-asset',
        asset: 'EUR',
      },
      [weth.symbol]: {
        type: 'market-price',
        providedBy: ['chainlink'],
      },
      [wsteth.symbol]: {
        type: 'market-price',
        providedBy: ['chainlink'],
      },
      [gno.symbol]: {
        type: 'market-price',
        providedBy: ['chainlink'],
      },
      [usdc.symbol]: {
        type: 'fixed',
      },
      [usdce.symbol]: {
        type: 'fixed',
      },
      [usdt.symbol]: {
        type: 'fixed',
      },
      [wxdai.symbol]: {
        type: 'fixed',
      },
      [sdai.symbol]: {
        type: 'yielding-fixed',
        baseAssetSymbol: TokenSymbol('DAI'),
        providedBy: ['chainlink'],
        oracleFetcher: fetchSdaiOracleInfoGnosis,
      },
    },
  },
  savings: {
    priceConfigs: {
      [xdai.symbol]: { type: 'fixed-usd' },
      [wxdai.symbol]: { type: 'fixed-usd' },
      [sdai.symbol]: { type: 'vault' },
    },
    accounts: sortSavingsAccounts([
      {
        savingsToken: sdai.symbol,
        underlyingToken: xdai.symbol,
        supportedAssets: [xdai.symbol],
        fetchConverterQuery: gnosisSavingsDaiConverterQuery,
        savingsRateQueryOptions: undefined,
        stats: {
          queryOptions: skySavingsGeneralStatsQueryOptionsFactory(sdai.symbol),
          items: ['tvl', 'users'] satisfies SavingsStatsItem[],
        },
        backedByQueryOptions: skySavingsBackedByOptions,
        liquidityChartQueryOptions: undefined,
        sparkPointsMultiplier: undefined,
      },
    ]),
    psmStables: undefined,
  },
  legacyFarms: undefined,
  spk: undefined,
  points: undefined,
  sparkRewards: undefined,
  swap: undefined,
}
